import cx from "classnames";
import { FC, JSX } from "react";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";

interface RoundCheckboxMultiSelectProps {
  /**
   * The label to show above the checkboxes.
   */
  label: string | JSX.Element;
  /**
   * The list of string option values to render as checkboxes.
   */
  values: string[];
  /**
   * Used for the QA automated test handle, and as the element's unique id.
   */
  testId: string;
  /**
   * When true, a "None" checkbox is prepended to the list. Selecting "None" clears all other
   * selections. Selecting any other option clears "None".
   */
  includesNone?: boolean;
  /**
   * The currently selected values. Pass an empty array when nothing is selected.
   * Use "none" to represent the "None" selection; meaning not having any of the other options selected.
   * Note that "none" is reserved and should not be used as a value in the values array.
   */
  selectedValues: string[];
  /**
   * Called with the full updated selection array whenever the user toggles a checkbox.
   */
  onChange: (selectedValues: string[]) => void;
  /**
   * Optional additional CSS class name(s) applied to the checkbox row container,
   * allowing callers to scope custom styles without affecting other usages.
   */
  className?: string;
}

/**
 * A component that renders a labeled row of checkboxes for string option values,
 * supporting multiple simultaneous selections. An optional "None" option is mutually
 * exclusive with all other options: selecting "None" clears the rest, and selecting
 * any other option clears "None".
 */
const NONE_VALUE = "none";

const RoundCheckboxMultiSelect: FC<RoundCheckboxMultiSelectProps> = ({
  label,
  values,
  testId,
  includesNone,
  selectedValues,
  onChange,
  className,
}) => {
  const handleNoneChange = () => {
    if (selectedValues.includes(NONE_VALUE)) {
      onChange(selectedValues.filter(v => v !== NONE_VALUE));
    } else {
      onChange([NONE_VALUE]);
    }
  };

  const handleValueChange = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues.filter(v => v !== NONE_VALUE), value]);
    }
  };

  return (
    <>
      <span className="round-checkbox-label">{label}</span>
      <div className={cx("round-checkbox-row", "round-checkbox-multi-select", className)}>
        {includesNone && (
          <span className="round-checkbox string-value" key={`${testId}-label-None`}>
            <QESCheckbox
              checked={selectedValues.includes(NONE_VALUE)}
              key={`${testId}-None`}
              label="None"
              onChange={handleNoneChange}
              testId={`${testId}-None`}
            />
          </span>
        )}
        {values.map((value) => (
          <span className="round-checkbox string-value" key={`${testId}-label-${value}`}>
            <QESCheckbox
              checked={selectedValues.includes(value)}
              key={`${testId}-${value}`}
              label={value}
              onChange={() => handleValueChange(value)}
              testId={`${testId}-${value}`}
            />
          </span>
        ))}
      </div>
    </>
  );
};

export default RoundCheckboxMultiSelect;
